

import { useState, useEffect, Suspense } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import Badge from "../../components/Badge";
import Modal from "../../components/Modal";
import { enquiriesApi, mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";

interface Enquiry {
  id: number;
  name: string;
  phone: string;
  email: string;
  event_date: string;
  guests: number;
  event_type: string;
  status: string;
  notes: string;
  lead_source: string;
  created_at: string;
  client_id?: number;
  package_id?: number;
  estimated_budget?: number;
  client?: { id: number; name: string };
}

function EnquiryDetailPageContent() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const enquiryId = Number(searchParams.get("id"));

  const [enquiry, setEnquiry] = useState<Enquiry | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    if (!enquiryId) return;
    loadEnquiry();
    loadEventTypes();
  }, [enquiryId]);

  const loadEnquiry = async () => {
    try {
      setLoading(true);
      const response = await enquiriesApi.get(enquiryId);
      setEnquiry(response.data?.data || response.data);
    } catch (error) {
      showToast("Failed to load enquiry", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadEventTypes = async () => {
    try {
      const response = await mastersApi.eventTypes.list({ status: "active" });
      const data = response.data?.data || response.data || [];
      setEventTypes(data.map((t: any) => t.name));
    } catch (error) {
      setEventTypes(["Wedding", "Corporate", "Birthday", "Social", "Other"]);
    }
  };

  const handleUpdateField = async (field: string, value: any) => {
    if (!enquiry) return;
    setSaving(true);
    try {
      const updateData: any = {};
      if (field === "name") updateData.client_name = value;
      else if (field === "phone") updateData.client_phone = value;
      else if (field === "email") updateData.client_email = value;
      else if (field === "event_date") updateData.event_date = value;
      else if (field === "event_type") updateData.type = value;
      else if (field === "guests") updateData.guest_count = parseInt(value) || 0;
      else if (field === "status") updateData.status = value;
      else updateData[field] = value;

      await enquiriesApi.update(enquiry.id, updateData);
      setEnquiry({ ...enquiry, [field]: value });
      setEditingField(null);
      showToast("Updated successfully", "success");
    } catch (error) {
      showToast("Failed to update", "error");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (field: string, value: any) => {
    setEditingField(field);
    setEditValue(String(value || ""));
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  const saveEditing = () => {
    if (editingField && enquiry) {
      handleUpdateField(editingField, editValue);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!enquiry) return;
    setSaving(true);
    try {
      await enquiriesApi.update(enquiry.id, { status: newStatus });
      setEnquiry({ ...enquiry, status: newStatus });
      showToast(`Status updated to ${newStatus}`, "success");
    } catch (error) {
      showToast("Failed to update status", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToEvent = async () => {
    if (!enquiry) return;
    try {
      await enquiriesApi.convert(enquiry.id, {});
      showToast("Enquiry converted to event successfully", "success");
      setEnquiry({ ...enquiry, status: "converted" });
      setShowConvertModal(false);
      navigate("/events");
    } catch (error) {
      showToast("Failed to convert enquiry", "error");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!enquiry) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">Enquiry Not Found</h2>
          <p className="text-stone-400 mb-4">The enquiry you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate("/enquiries")}
            className="px-4 py-2 rounded-xl gold-gradient text-white font-semibold text-sm"
          >
            ← Back to Enquiries
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const statusActions: Record<string, string[]> = {
    new: ["qualified", "rejected"],
    qualified: ["converted", "rejected"],
    converted: [],
    rejected: ["new", "qualified"],
  };

  const EditableField = ({
    label,
    field,
    value,
    type = "text",
    options,
  }: {
    label: string;
    field: string;
    value: any;
    type?: string;
    options?: { value: string; label: string }[];
  }) => (
    <div className="group relative">
      <label className="text-xs text-stone-400 block mb-1">{label}</label>
      {editingField === field ? (
        <div className="flex gap-2">
          {type === "select" && options ? (
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-amber-300 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              autoFocus
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <input
              type={type}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded-lg border border-amber-300 text-sm focus:border-amber-400 focus:ring-1 focus:ring-amber-200 outline-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") saveEditing();
                if (e.key === "Escape") cancelEditing();
              }}
            />
          )}
          <button
            onClick={saveEditing}
            disabled={saving}
            className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-medium hover:bg-emerald-200"
          >
            ✓
          </button>
          <button
            onClick={cancelEditing}
            className="px-2 py-1 rounded-lg bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200"
          >
            ✕
          </button>
        </div>
      ) : (
        <div
          onClick={() => startEditing(field, value)}
          className="cursor-pointer hover:bg-amber-50 px-2 py-1.5 rounded-lg border border-transparent hover:border-amber-200 transition-colors -mx-2"
        >
          <span className="text-sm font-medium text-stone-800">{value || "-"}</span>
          <span className="ml-2 opacity-0 group-hover:opacity-100 text-amber-500 text-xs">✎</span>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enquiries")}
            className="p-2 rounded-xl bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-stone-800 font-serif">{enquiry.name}</h1>
            <p className="text-xs text-stone-400">#{enquiry.id} • Created {enquiry.created_at ? new Date(enquiry.created_at).toLocaleDateString() : "-"}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge label={enquiry.status} status={enquiry.status} />
          {enquiry.status !== "converted" && enquiry.status !== "rejected" && (
            <button
              onClick={() => navigate(`/quotations/new?enquiry_id=${enquiry.id}`)}
              className="text-sm font-semibold text-white px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 transition-colors"
            >
              Create Quotation
            </button>
          )}
        </div>
      </div>

      {/* Compact Info Card */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Contact Info */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide border-b border-stone-100 pb-2">Contact</h3>
            <EditableField label="Phone" field="phone" value={enquiry.phone} type="tel" />
            <EditableField label="Email" field="email" value={enquiry.email} type="email" />
          </div>

          {/* Event Details */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide border-b border-stone-100 pb-2">Event</h3>
            <EditableField
              label="Type"
              field="event_type"
              value={enquiry.event_type}
              type="select"
              options={eventTypes.map((t) => ({ value: t, label: t }))}
            />
            <EditableField label="Date" field="event_date" value={enquiry.event_date} type="date" />
            <EditableField label="Guests" field="guests" value={enquiry.guests} type="number" />
          </div>

          {/* Source & Budget */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide border-b border-stone-100 pb-2">Source</h3>
            <div>
              <label className="text-xs text-stone-400 block mb-1">Lead Source</label>
              <span className="text-sm font-medium text-stone-800">{enquiry.lead_source || "-"}</span>
            </div>
            <div>
              <label className="text-xs text-stone-400 block mb-1">Client</label>
              <span className="text-sm font-medium text-stone-800">{enquiry.client?.name || enquiry.name}</span>
            </div>
          </div>

          {/* Status & Actions */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide border-b border-stone-100 pb-2">Status</h3>
            <div>
              <label className="text-xs text-stone-400 block mb-1">Current Status</label>
              <span className="text-sm font-medium capitalize">{enquiry.status}</span>
            </div>
            {statusActions[enquiry.status]?.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-stone-400">Update to:</p>
                <div className="flex flex-wrap gap-2">
                  {statusActions[enquiry.status].map((action) => (
                    <button
                      key={action}
                      onClick={() => handleStatusChange(action)}
                      disabled={saving}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                        action === "converted"
                          ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                          : action === "qualified"
                          ? "bg-violet-100 text-violet-700 hover:bg-violet-200"
                          : action === "rejected"
                          ? "bg-rose-100 text-rose-700 hover:bg-rose-200"
                          : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                      }`}
                    >
                      {action.charAt(0).toUpperCase() + action.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {enquiry.notes && (
          <div className="mt-4 pt-4 border-t border-stone-100">
            <label className="text-xs text-stone-400 block mb-1">Notes</label>
            <p className="text-sm text-stone-600">{enquiry.notes}</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5">
        <h3 className="text-sm font-semibold text-stone-800 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => navigate(`/quotations/new?enquiry_id=${enquiry.id}`)}
            className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors text-left"
          >
            <div className="text-2xl mb-2">📋</div>
            <div className="text-sm font-semibold text-emerald-800">Create Quotation</div>
            <div className="text-xs text-emerald-600">Generate a quote for this enquiry</div>
          </button>
          
          {enquiry.status !== "converted" && (
            <button
              onClick={() => setShowConvertModal(true)}
              className="p-4 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors text-left"
            >
              <div className="text-2xl mb-2">🎉</div>
              <div className="text-sm font-semibold text-amber-800">Convert to Event</div>
              <div className="text-xs text-amber-600">Turn this into a booking</div>
            </button>
          )}

          <button
            onClick={() => navigate(`/clients?search=${encodeURIComponent(enquiry.name)}`)}
            className="p-4 rounded-xl bg-sky-50 border border-sky-200 hover:bg-sky-100 transition-colors text-left"
          >
            <div className="text-2xl mb-2">👤</div>
            <div className="text-sm font-semibold text-sky-800">View Client</div>
            <div className="text-xs text-sky-600">See client profile</div>
          </button>

          <button
            onClick={() => {
              if (confirm("Are you sure you want to delete this enquiry?")) {
                enquiriesApi.delete(enquiry.id).then(() => {
                  showToast("Enquiry deleted", "success");
                  navigate("/enquiries");
                }).catch(() => showToast("Failed to delete", "error"));
              }
            }}
            className="p-4 rounded-xl bg-rose-50 border border-rose-200 hover:bg-rose-100 transition-colors text-left"
          >
            <div className="text-2xl mb-2">🗑️</div>
            <div className="text-sm font-semibold text-rose-800">Delete Enquiry</div>
            <div className="text-xs text-rose-600">Remove this enquiry</div>
          </button>
        </div>
      </div>

      {/* Convert to Event Modal */}
      <Modal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        title="Convert to Event"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-stone-600">
            This will convert the enquiry to an event. The enquiry status will be updated to &quot;converted&quot;.
          </p>
          <div className="bg-stone-50 rounded-xl p-4">
            <div className="text-xs text-stone-400 mb-1">Client</div>
            <div className="font-semibold text-stone-800">{enquiry.name}</div>
            <div className="text-xs text-stone-500 mt-2">{enquiry.event_type} • {enquiry.event_date}</div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowConvertModal(false)}
              className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleConvertToEvent}
              className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient"
            >
              Convert
            </button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}

function LoadingFallback() {
  return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
      </div>
    </DashboardLayout>
  );
}

function EnquiryDetailPageWrapper() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EnquiryDetailPageContent />
    </Suspense>
  );
}

export default function EnquiryDetailPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <EnquiryDetailPageWrapper />
    </Suspense>
  );
}
